import type { CSSProperties, ReactElement, ReactNode } from 'react';
import {
  cloneElement,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled, { css } from 'styled-components';
import type { FontValue } from '@elfsight/configurator-contracts';
import { useDelayedUnmount } from '@elfsight/lib-utils';
import { useShadowGlobalProps } from '@elfsight/widget-shadow';
import { createReadableClass } from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';
import { useRect } from 'react-use-rect';
import { Backdrop } from '../backdrop';
import { Portal } from '../portal';
import {
  consumeEvent,
  findRelativeParent,
  findScrollTargets,
  useOutsideClick,
  useScrollLock
} from '../utils';
import { zIndexLayer } from '../zIndexLayer';
import {
  computeOffsetParentLayout,
  computeViewportFixedLayout
} from './popout-layout';
import type { Direction } from './popout-types';

const ANIMATION_DURATION_MS = 300;
const CLOSING_CLASS = 'popout-content-closing';

export interface PopoutContentInnerProperties {
  effectiveDirection: Direction;
  anchorWidth: number;
  anchorHeight: number;
}

export interface PopoutProps {
  show: boolean;
  anchorElement: Element | null;
  children?:
    | ReactNode
    | ((innerProperties: PopoutContentInnerProperties) => ReactNode);
  direction?: Direction;
  alignment?: 'left' | 'center' | 'right';
  offsetTop?: number;
  offsetLeft?: number;
  disableScroll?: boolean;
  closeOnOutsideClick?: boolean;
  preventCloseOnAnchorElementClick?: boolean;
  withBackdrop?: boolean;
  backdropComponent?: ReactElement;
  backdropColor?: string;
  withoutTransition?: boolean;
  zIndex?: number;
  portalContainerElement?: Element;
  contentStyles?: CSSProperties;
  shadow?: boolean;
  onCloseRequest?(event?: MouseEvent | PointerEvent): void;
  font?: FontValue;
}

export const Popout = forwardRef<HTMLDivElement, PopoutProps>(
  (
    {
      show,
      children,
      anchorElement,
      direction = 'downward',
      alignment = 'left',
      offsetTop = 0,
      offsetLeft = 0,
      disableScroll = false,
      closeOnOutsideClick = false,
      preventCloseOnAnchorElementClick = false,
      withBackdrop = false,
      backdropComponent,
      backdropColor,
      withoutTransition = false,
      zIndex = zIndexLayer.modal,
      portalContainerElement,
      contentStyles,
      shadow,
      onCloseRequest,
      font
    },
    forwardingRef
  ) => {
    const { mode: isShadow } = useShadowGlobalProps() ?? {};

    const hasDocumentPortalContainer =
      !portalContainerElement ||
      portalContainerElement === document.body ||
      portalContainerElement === document.documentElement;

    const inShadowDom =
      !!isShadow || anchorElement?.getRootNode() instanceof ShadowRoot;

    const layoutUsesViewport = hasDocumentPortalContainer && inShadowDom;

    const [anchorRect, setAnchorRect] = useState({
      width: 0,
      height: 0,
      top: 0,
      left: 0
    });
    const [anchorRectRef] = useRect(setAnchorRect, {
      resize: true
    });

    // start to measure the anchor element when Popout showed
    useEffect(() => {
      if (!show) {
        return;
      }

      anchorRectRef(anchorElement);
      return () => anchorRectRef(null);
    }, [show, anchorElement, anchorRectRef]);

    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);

    const [contentRect, setContainerRect] = useState({ width: 0, height: 0 });

    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const contentRectRef = useCallback((node: HTMLDivElement | null) => {
      resizeObserverRef.current?.disconnect();

      if (node) {
        setContainerRect(node.getBoundingClientRect());

        resizeObserverRef.current = new ResizeObserver(() => {
          setContainerRect(node.getBoundingClientRect());
        });
        resizeObserverRef.current.observe(node);
      }
    }, []);

    const scrollTargets = useMemo(() => {
      if (inShadowDom) {
        return findScrollTargets(anchorElement);
      } else {
        const root = anchorElement?.getRootNode();

        if (!root) {
          return;
        }

        if (root instanceof ShadowRoot) {
          return [root.getRootNode()];
        }

        return [root];
      }
    }, [anchorElement]);

    useEffect(() => {
      let rafId: number | null = null;

      const handleScroll = () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }

        rafId = requestAnimationFrame(() => {
          if (contentRef.current) {
            setContainerRect(contentRef.current.getBoundingClientRect());
          }
        });
      };

      if (!scrollTargets) {
        return;
      }

      for (const target of scrollTargets) {
        target.addEventListener('scroll', handleScroll, {
          passive: true,
          capture: true
        });
      }

      return () => {
        for (const target of scrollTargets) {
          target.removeEventListener('scroll', handleScroll, {
            capture: true
          });
        }

        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
      };
    }, [scrollTargets]);

    const setContentXY = useCallback((x: number, y: number) => {
      const { current: contentElement } = contentRef;

      if (!contentElement) {
        return;
      }

      contentElement.style.transform = `translate(${Math.round(
        x
      )}px, ${Math.round(y)}px)`;
    }, []);

    const effectiveDirection = useMemo(() => {
      if (!show) {
        return direction;
      }

      const box = {
        anchorWidth: anchorRect.width,
        anchorHeight: anchorRect.height,
        anchorLeft: anchorRect.left,
        anchorTop: anchorRect.top,
        contentWidth: contentRect.width,
        contentHeight: contentRect.height
      };

      const offsets = {
        direction,
        alignment,
        offsetTop,
        offsetLeft
      };

      let layoutResult:
        | ReturnType<typeof computeViewportFixedLayout>
        | ReturnType<typeof computeOffsetParentLayout>;

      if (layoutUsesViewport) {
        layoutResult = computeViewportFixedLayout(box, offsets);
      } else {
        const wrapperElement = wrapperRef.current;

        if (!wrapperElement) {
          return direction;
        }

        const [closestRelative, secondRelative] =
          findRelativeParent(wrapperElement) ?? [];

        if (!closestRelative) {
          return direction;
        }

        const relativeBoundaryRect = closestRelative.getBoundingClientRect();
        let secondRelativeBoundaryRectTop;

        // В некоторых виджетах Portal (и, соответственно, Popout) рендерится внутри shadow DOM.
        // В таких случаях boundary-элементом будет EmbedShadowRoot,
        // что может вызывать проблемы с вертикальным позиционированием:
        // DOMRect EmbedShadowRoot не совпадает с DOMRect элемента,
        // который был бы выбран без shadow DOM.
        // Поэтому берём значение "top" у второго родительского relative-элемента.
        if (inShadowDom && secondRelative) {
          secondRelativeBoundaryRectTop =
            secondRelative.getBoundingClientRect().top;
        }

        layoutResult = computeOffsetParentLayout({
          box,
          relativeBoundaryRect,
          secondRelativeBoundaryRectTop,
          options: offsets
        });
      }

      const { nextX, nextY, effectiveDirection: nextDirection } = layoutResult;

      setContentXY(nextX, nextY);

      return nextDirection;
    }, [
      anchorRect,
      contentRect,
      show,
      offsetTop,
      offsetLeft,
      direction,
      alignment,
      layoutUsesViewport,
      setContentXY
    ]);

    // handle close on click outside
    const outsideClickRef = useOutsideClick((event) => {
      if (!show || !closeOnOutsideClick || !onCloseRequest) {
        return;
      }

      const path = event.composedPath();
      const eventTargetIsAnchorElement =
        anchorElement && path.includes(anchorElement);

      if (!preventCloseOnAnchorElementClick || !eventTargetIsAnchorElement) {
        onCloseRequest(event);

        if (isShadow || eventTargetIsAnchorElement) {
          consumeEvent(event);
        }
      }
    });

    // merge all the content refs
    const contentMergedRef = useMergedRef(
      contentRectRef,
      outsideClickRef,
      contentRef
    );

    // disable the document scroll
    useScrollLock(wrapperRef, disableScroll && show);

    const wrapperMergedRef = useMergedRef(forwardingRef, wrapperRef);

    // handle delayed unmount for animations
    const animationDuration = withoutTransition ? 0 : ANIMATION_DURATION_MS;
    const [isMounted, isClosing] = useDelayedUnmount(show, animationDuration);
    const isVisible = show && !isClosing;

    // init inner properties
    const innerProperties: PopoutContentInnerProperties = {
      effectiveDirection,
      anchorWidth: anchorRect.width,
      anchorHeight: anchorRect.height
    };

    const FinalBackdrop = backdropComponent ? (
      cloneElement(backdropComponent, {
        ...backdropComponent.props,
        style: {
          zIndex,
          ...backdropComponent.props.style
        }
      })
    ) : (
      <Backdrop
        background={backdropColor}
        zIndex={zIndex}
        open={show}
        withoutTransition={withoutTransition}
        fixed
      />
    );

    return (
      isMounted && (
        <Portal containerElement={portalContainerElement} shadow={shadow}>
          <PopoutWrapper
            ref={wrapperMergedRef}
            className={createReadableClass({
              component: 'popout',
              element: 'wrapper'
            })}
            _font={font}
          >
            {withBackdrop && FinalBackdrop}

            <PopoutContent
              _position={layoutUsesViewport ? 'fixed' : 'absolute'}
              _animationDuration={animationDuration}
              ref={contentMergedRef}
              className={createReadableClass(
                {
                  component: 'popout',
                  element: 'content'
                },
                [isVisible ? '' : CLOSING_CLASS]
              )}
              style={{ zIndex, ...contentStyles }}
            >
              {typeof children === 'function'
                ? children(innerProperties)
                : children}
            </PopoutContent>
          </PopoutWrapper>
        </Portal>
      )
    );
  }
);

const PopoutWrapper = styled.div<{ _font?: FontValue }>`
  ${({ _font }) => !!_font?.value && `font-family: ${_font.value};`}
`;

const PopoutContent = styled.div<{
  _animationDuration: number;
  _position: 'fixed' | 'absolute';
}>`
  ${({ _animationDuration, _position }) => css`
    position: ${_position};
    top: 0;
    left: 0;
    transition: opacity ${_animationDuration}ms ease-out;

    &.${CLOSING_CLASS} {
      opacity: 0;
    }

    @starting-style {
      & {
        opacity: 0;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      & {
        transition: none;
      }
    }
  `}
`;
